import React from 'react';

/**
 * Props for `ColorPairButton`.
 *
 * @property pocketColor - Hex color currently assigned to the pocket component.
 * @property chainColor  - Hex color currently assigned to the chain component.
 * @property active      - Whether a quick-color pair is currently active for this row.
 *                         When `false` the button renders a neutral icon; when `true`
 *                         it renders two colored halves (pocket | chain).
 * @property onClick     - Click handler; should call `selectNextColorPair` on the base class.
 * @property title       - Optional tooltip override.
 */
interface ColorPairButtonProps {
   pocketColor: string;
   chainColor: string;
   active: boolean;
   onClick: (e: React.MouseEvent) => void;
   title?: string;
}

/**
 * Button displayed in each table row for the quick-color feature.
 * When `active` is `false` it shows a neutral ring icon prompting the user to assign a
 * color pair. When `active` is `true` it renders two colored half-circles (pocket on the
 * left, chain on the right) so the user can see the active pair at a glance.
 */
const ColorPairButton = ({
   pocketColor,
   chainColor,
   active,
   onClick,
   title,
}: ColorPairButtonProps): React.JSX.Element => (
   <button
      className={`color-pair-btn color-pair-btn--cell${active ? ' color-pair-btn--active' : ''}`}
      title={title ?? (active ? `Pocket: ${pocketColor} | Chain: ${chainColor}` : 'Apply color pair')}
      onClick={onClick}
   >
      {active ? (
         <>
            <span className="color-pair-btn__half color-pair-btn__pocket" style={{ background: pocketColor }} />
            <span className="color-pair-btn__half color-pair-btn__chain" style={{ background: chainColor }} />
         </>
      ) : (
         <span className="color-pair-btn__inactive-icon" aria-hidden="true">
            ◉
         </span>
      )}
   </button>
);

export default ColorPairButton;
